import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const LFO_WAVEFORMS = Object.freeze(['sine', 'triangle', 'saw', 'pulse'] as const);
export type LfoWaveform = (typeof LFO_WAVEFORMS)[number];

export const LFO_RATE_MODES = Object.freeze(['free', 'transport-sync'] as const);
export type LfoRateMode = (typeof LFO_RATE_MODES)[number];

export const LFO_POLARITIES = Object.freeze(['bipolar', 'unipolar'] as const);
export type LfoPolarity = (typeof LFO_POLARITIES)[number];

/**
 * Straight, triplet (`T`) and dotted (`.`) note divisions. A `1/4` cycle is
 * one quarter-note beat; `1/1` is four beats and `2/1` is eight beats.
 */
export const TRANSPORT_DIVISIONS = Object.freeze([
  '1/64',
  '1/32T',
  '1/32',
  '1/16T',
  '1/16',
  '1/8T',
  '1/16.',
  '1/8',
  '1/4T',
  '1/8.',
  '1/4',
  '1/2T',
  '1/4.',
  '1/2',
  '1/1T',
  '1/2.',
  '1/1',
  '2/1',
  '4/1',
] as const);
export type TransportDivision = (typeof TRANSPORT_DIVISIONS)[number];

export interface LfoOptions {
  readonly sampleRate: number;
  readonly waveform?: LfoWaveform;
  readonly rateMode?: LfoRateMode;
  readonly rateHz?: number;
  readonly tempoBpm?: number;
  readonly division?: TransportDivision;
  readonly resetPhase?: number;
  readonly polarity?: LfoPolarity;
  readonly pulseWidth?: number;
  readonly smoothingSeconds?: number;
}

const WAVEFORM_BY_INDEX: Readonly<Record<number, LfoWaveform>> = Object.freeze({
  0: 'sine',
  1: 'triangle',
  2: 'saw',
  3: 'pulse',
});
const RATE_MODE_BY_INDEX: Readonly<Record<number, LfoRateMode>> = Object.freeze({
  0: 'free',
  1: 'transport-sync',
});
const POLARITY_BY_INDEX: Readonly<Record<number, LfoPolarity>> = Object.freeze({
  0: 'bipolar',
  1: 'unipolar',
});
const DIVISION_BY_INDEX: Readonly<Record<number, TransportDivision>> = Object.freeze(
  Object.fromEntries(TRANSPORT_DIVISIONS.map((division, index) => [index, division])),
);

const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
};

const assertEnum = <T extends string>(label: string, value: string, values: readonly T[]): T => {
  if (!(values as readonly string[]).includes(value)) throw new RangeError(`${label} is invalid`);
  return value as T;
};

const indexedEnum = <T extends string>(
  label: string,
  value: number,
  values: Readonly<Record<number, T>>,
): T => {
  if (!Number.isInteger(value) || values[value] === undefined) {
    throw new RangeError(`${label} must be a valid integer enum index`);
  }
  return values[value]!;
};

export const divisionBeats = (division: TransportDivision): number => {
  const match = /^(\d+)\/(\d+)(T|\.)?$/.exec(division);
  if (!match) throw new RangeError('division is invalid');
  const numerator = Number(match[1]);
  const denominator = Number(match[2]);
  let beats = (4 * numerator) / denominator;
  if (match[3] === 'T') beats *= 2 / 3;
  else if (match[3] === '.') beats *= 1.5;
  return beats;
};

// Module-load preparation only; reset/setters can run at a live graph boundary
// without RegExp.exec allocating a match array on the audio callback.
const CACHED_DIVISION_BEATS = Object.freeze(Object.fromEntries(
  TRANSPORT_DIVISIONS.map(division => [division, divisionBeats(division)]),
)) as Readonly<Record<TransportDivision, number>>;

const waveformValue = (waveform: LfoWaveform, phase: number, pulseWidth: number): number => {
  if (waveform === 'sine') return Math.sin(phase * 2 * Math.PI);
  if (waveform === 'triangle') return 1 - 4 * Math.abs(phase - 0.5);
  if (waveform === 'saw') return 2 * phase - 1;
  return phase < pulseWidth ? 1 : -1;
};

const applyPolarity = (value: number, polarity: LfoPolarity): number => (
  polarity === 'unipolar' ? (value + 1) * 0.5 : value
);

/**
 * Host-independent, allocation-free LFO core. Phase, smoothing and reset-edge
 * state survive arbitrary block boundaries, so segmented and monolithic
 * rendering are sample-identical.
 */
export class Lfo {
  readonly sampleRate: number;

  private waveform: LfoWaveform;
  private rateMode: LfoRateMode;
  private rateHz: number;
  private tempoBpm: number;
  private division: TransportDivision;
  private configuredResetPhase: number;
  private polarity: LfoPolarity;
  private pulseWidth: number;
  private smoothingSeconds: number;

  private phase: number;
  private resetHigh = false;
  private currentFrequency: number;
  private targetFrequency: number;
  private frequencyStep = 0;
  private frequencyRampRemaining = 0;
  private currentPulseWidth: number;
  private pulseWidthStep = 0;
  private pulseWidthRampRemaining = 0;
  private transitionFromWaveform: LfoWaveform;
  private transitionFromPolarity: LfoPolarity;
  private transitionLength = 0;
  private transitionRemaining = 0;

  constructor(options: LfoOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000);
    this.sampleRate = options.sampleRate;
    this.waveform = assertEnum('waveform', options.waveform ?? 'sine', LFO_WAVEFORMS);
    this.rateMode = assertEnum('rateMode', options.rateMode ?? 'free', LFO_RATE_MODES);
    this.rateHz = options.rateHz ?? 1;
    this.tempoBpm = options.tempoBpm ?? 120;
    this.division = assertEnum('division', options.division ?? '1/4', TRANSPORT_DIVISIONS);
    this.configuredResetPhase = options.resetPhase ?? 0;
    this.polarity = assertEnum('polarity', options.polarity ?? 'bipolar', LFO_POLARITIES);
    this.pulseWidth = options.pulseWidth ?? 0.5;
    this.smoothingSeconds = options.smoothingSeconds ?? 0.01;
    this.validateConfiguration();

    this.phase = this.configuredResetPhase;
    this.targetFrequency = this.effectiveFrequency();
    this.currentFrequency = this.targetFrequency;
    this.currentPulseWidth = this.pulseWidth;
    this.transitionFromWaveform = this.waveform;
    this.transitionFromPolarity = this.polarity;
  }

  reset(): void {
    this.phase = this.configuredResetPhase;
    this.resetHigh = false;
    this.targetFrequency = this.effectiveFrequency();
    this.currentFrequency = this.targetFrequency;
    this.frequencyStep = 0;
    this.frequencyRampRemaining = 0;
    this.currentPulseWidth = this.pulseWidth;
    this.pulseWidthStep = 0;
    this.pulseWidthRampRemaining = 0;
    this.transitionFromWaveform = this.waveform;
    this.transitionFromPolarity = this.polarity;
    this.transitionLength = 0;
    this.transitionRemaining = 0;
  }

  resetPhase(phase = this.configuredResetPhase): void {
    assertRange('phase', phase, 0, 1);
    this.phase = phase === 1 ? 0 : phase;
  }

  setParameter(id: string, value: number): void {
    if (id === 'waveform') {
      const next = indexedEnum(id, value, WAVEFORM_BY_INDEX);
      if (next !== this.waveform) this.beginDiscreteTransition();
      this.waveform = next;
      return;
    }
    if (id === 'rate-mode') {
      this.rateMode = indexedEnum(id, value, RATE_MODE_BY_INDEX);
      this.scheduleFrequency();
      return;
    }
    if (id === 'rate-hz') {
      assertRange(id, value, 0.001, 100);
      this.rateHz = value;
      this.scheduleFrequency();
      return;
    }
    if (id === 'tempo-bpm') {
      assertRange(id, value, 20, 400);
      this.tempoBpm = value;
      this.scheduleFrequency();
      return;
    }
    if (id === 'division') {
      this.division = indexedEnum(id, value, DIVISION_BY_INDEX);
      this.scheduleFrequency();
      return;
    }
    if (id === 'reset-phase') {
      assertRange(id, value, 0, 1);
      this.configuredResetPhase = value === 1 ? 0 : value;
      return;
    }
    if (id === 'polarity') {
      const next = indexedEnum(id, value, POLARITY_BY_INDEX);
      if (next !== this.polarity) this.beginDiscreteTransition();
      this.polarity = next;
      return;
    }
    if (id === 'pulse-width') {
      assertRange(id, value, 0.01, 0.99);
      this.pulseWidth = value;
      this.schedulePulseWidth();
      return;
    }
    if (id === 'smoothing-seconds') {
      assertRange(id, value, 0, 1);
      this.smoothingSeconds = value;
      return;
    }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  processInto(output: Float32Array, reset?: Float32Array, frames = output.length): void {
    if (!Number.isInteger(frames) || frames < 0 || output.length < frames || (reset && reset.length < frames)) throw new RangeError('buffers must cover frames');
    if (reset) {
      for (let index = 0; index < frames; index += 1) {
        if (!Number.isFinite(reset[index])) throw new RangeError('reset controls must be finite');
      }
    }
    for (let index = 0; index < frames; index += 1) {
      const high = (reset?.[index] ?? 0) > 0;
      if (high && !this.resetHigh) this.resetPhase();
      this.resetHigh = high;

      this.advanceRamps();
      const target = applyPolarity(
        waveformValue(this.waveform, this.phase, this.currentPulseWidth),
        this.polarity,
      );
      let value = target;
      if (this.transitionRemaining > 0) {
        const from = applyPolarity(
          waveformValue(this.transitionFromWaveform, this.phase, this.currentPulseWidth),
          this.transitionFromPolarity,
        );
        const progress = 1 - this.transitionRemaining / this.transitionLength;
        value = from + (target - from) * progress;
        this.transitionRemaining -= 1;
      }
      output[index] = Math.max(-1, Math.min(1, value));

      this.phase += this.currentFrequency / this.sampleRate;
      this.phase -= Math.floor(this.phase);
    }
  }

  private validateConfiguration(): void {
    assertRange('rateHz', this.rateHz, 0.001, 100);
    assertRange('tempoBpm', this.tempoBpm, 20, 400);
    assertRange('resetPhase', this.configuredResetPhase, 0, 1);
    if (this.configuredResetPhase === 1) this.configuredResetPhase = 0;
    assertRange('pulseWidth', this.pulseWidth, 0.01, 0.99);
    assertRange('smoothingSeconds', this.smoothingSeconds, 0, 1);
  }

  private effectiveFrequency(): number {
    if (this.rateMode === 'free') return this.rateHz;
    return (this.tempoBpm / 60) / CACHED_DIVISION_BEATS[this.division];
  }

  private rampSamples(): number {
    return Math.max(0, Math.round(this.smoothingSeconds * this.sampleRate));
  }

  private scheduleFrequency(): void {
    this.targetFrequency = this.effectiveFrequency();
    const samples = this.rampSamples();
    if (samples === 0) {
      this.currentFrequency = this.targetFrequency;
      this.frequencyStep = 0;
      this.frequencyRampRemaining = 0;
      return;
    }
    this.frequencyStep = (this.targetFrequency - this.currentFrequency) / samples;
    this.frequencyRampRemaining = samples;
  }

  private schedulePulseWidth(): void {
    const samples = this.rampSamples();
    if (samples === 0) {
      this.currentPulseWidth = this.pulseWidth;
      this.pulseWidthStep = 0;
      this.pulseWidthRampRemaining = 0;
      return;
    }
    this.pulseWidthStep = (this.pulseWidth - this.currentPulseWidth) / samples;
    this.pulseWidthRampRemaining = samples;
  }

  private beginDiscreteTransition(): void {
    if (this.transitionRemaining === 0) {
      this.transitionFromWaveform = this.waveform;
      this.transitionFromPolarity = this.polarity;
    }
    this.transitionLength = this.rampSamples();
    this.transitionRemaining = this.transitionLength;
  }

  private advanceRamps(): void {
    if (this.frequencyRampRemaining > 0) {
      this.currentFrequency += this.frequencyStep;
      this.frequencyRampRemaining -= 1;
      if (this.frequencyRampRemaining === 0) this.currentFrequency = this.targetFrequency;
    }
    if (this.pulseWidthRampRemaining > 0) {
      this.currentPulseWidth += this.pulseWidthStep;
      this.pulseWidthRampRemaining -= 1;
      if (this.pulseWidthRampRemaining === 0) this.currentPulseWidth = this.pulseWidth;
    }
  }
}

class LfoModuleInstance implements DspModuleInstance {
  private readonly lfo: Lfo;
  private readonly maxBlockSize: number;

  constructor(sampleRate: number, maxBlockSize: number) {
    this.maxBlockSize = maxBlockSize;
    this.lfo = new Lfo({ sampleRate });
  }

  reset(): void { this.lfo.reset(); }
  setParameter(id: string, value: number): void { this.lfo.setParameter(id, value); }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
    const output = block.outputs.out;
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    const reset = block.controls.reset;
    if (reset && reset.length < block.frames) throw new RangeError('reset buffer is too short');
    this.lfo.processInto(output, reset, block.frames);
  }
}

export const mod015 = defineDspModule({
  descriptor: {
    id: 'mir.mod-015',
    name: 'LFO',
    version: '0.1.0',
    inputs: [{ id: 'reset', kind: 'control', channels: 1 }],
    outputs: [{ id: 'out', kind: 'control', channels: 1 }],
    parameters: [
      { id: 'waveform', defaultValue: 0, minValue: 0, maxValue: 3, unit: 'enum' },
      { id: 'rate-mode', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'enum' },
      { id: 'rate-hz', defaultValue: 1, minValue: 0.001, maxValue: 100, unit: 'Hz' },
      { id: 'tempo-bpm', defaultValue: 120, minValue: 20, maxValue: 400, unit: 'BPM' },
      { id: 'division', defaultValue: 10, minValue: 0, maxValue: TRANSPORT_DIVISIONS.length - 1, unit: 'enum' },
      { id: 'reset-phase', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'cycle' },
      { id: 'polarity', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'enum' },
      { id: 'pulse-width', defaultValue: 0.5, minValue: 0.01, maxValue: 0.99, unit: 'ratio' },
      { id: 'smoothing-seconds', defaultValue: 0.01, minValue: 0, maxValue: 1, unit: 's' },
    ],
  },
  createInstance(context) {
    if (!(context.sampleRate > 0) || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new LfoModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
